import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, ShieldAlert, Loader2, Check, Users, Music2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { adminCreateAccount } from "@/lib/afmApi";
import { SectionTitle } from "@/components/common";

const ADMIN_ROLES = ["admin", "distribution_admin", "superadmin", "staff"];
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-black outline-none transition-colors placeholder:text-slate-400 focus:border-[#9E1B22]";
const strong = (p) => p.length >= 8 && /[A-Z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p);

const SCOPES = [
  { key: "afm", title: "Solo Always Fresh Music", desc: "Acceso únicamente al portal de distribución AFM." },
  { key: "both", title: "SONA + Always Fresh Music", desc: "Acceso completo: estudio SONA y portal AFM." },
];

export default function AdminAccounts() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role) || user?.super_admin;
  const [f, setF] = useState({ name: "", email: "", password: "", scope: "afm", artist_name: "", country: "", make_admin: false });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  if (!isAdmin) return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="admin-denied">
      <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
      <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-500">Esta sección es exclusiva del equipo de Always Fresh.</p>
    </div>
  );

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Completa nombre y correo"); return; }
    if (!strong(f.password)) { toast.error("La contraseña debe tener 8+ caracteres, una mayúscula, un número y un símbolo"); return; }
    setBusy(true);
    try {
      const u = await adminCreateAccount({ viewer_id: user.id, ...f, email: f.email.trim().toLowerCase() });
      setCreated(u);
      toast.success(`Cuenta creada: ${u.email}`);
      setF({ name: "", email: "", password: "", scope: "afm", artist_name: "", country: "", make_admin: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la cuenta");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-accounts-page">
      <SectionTitle icon={UserPlus} title="Gestión de Cuentas" desc="Crea cuentas nuevas y define su alcance. Los super admin pueden además dar/quitar admin y revisar artistas." />

      {created && (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="account-created-banner">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <div className="font-semibold">Cuenta creada: {created.email}</div>
            <div className="mt-0.5 text-xs text-emerald-800">
              Acceso: {created.sona_access ? "SONA + Always" : "Solo Always"}{created.made_admin ? " · Admin otorgado" : ""}. La persona puede entrar con la contraseña asignada o recuperarla desde "¿Olvidaste tu contraseña?".
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre completo</label>
            <input data-testid="acc-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Nombre y apellido" className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Correo electrónico</label>
            <input data-testid="acc-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="correo@dominio.com" className={inputCls} />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Contraseña inicial</label>
          <div className="relative">
            <input data-testid="acc-password" type={show ? "text" : "password"} value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" className={`${inputCls} pr-10`} />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black" data-testid="acc-password-toggle">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Mínimo 8 caracteres, con mayúscula, número y símbolo. La persona podrá cambiarla luego.</p>
        </div>

        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-600">¿Hacia dónde dar acceso?</label>
          <div className="grid gap-3 sm:grid-cols-2">
            {SCOPES.map((s) => {
              const on = f.scope === s.key;
              return (
                <button key={s.key} type="button" data-testid={`acc-scope-${s.key}`} onClick={() => set("scope", s.key)}
                  className={`rounded-xl border p-4 text-left transition-colors ${on ? "border-[#9E1B22] bg-[#9E1B22]/5 ring-1 ring-[#9E1B22]" : "border-slate-300 hover:border-slate-400"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-black">{s.title}</span>
                    {on && <Check className="h-4 w-4 text-[#9E1B22]" />}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{s.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {f.scope === "afm" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre artístico <span className="text-slate-400">(opcional)</span></label>
              <input data-testid="acc-artist-name" value={f.artist_name} onChange={(e) => set("artist_name", e.target.value)} placeholder="Nombre de artista" className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">País <span className="text-slate-400">(opcional)</span></label>
              <input data-testid="acc-country" value={f.country} onChange={(e) => set("country", e.target.value)} placeholder="Venezuela" className={inputCls} />
            </div>
          </div>
        )}

        <label data-testid="acc-make-admin-label" className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <input data-testid="acc-make-admin" type="checkbox" checked={f.make_admin} onChange={(e) => set("make_admin", e.target.checked)} className="h-4 w-4 accent-[#9E1B22]" />
          <span>Hacer <strong>administrador</strong> de Always Fresh (acceso total al panel admin).</span>
        </label>

        <button type="submit" data-testid="acc-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#9E1B22] px-4 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Crear cuenta
        </button>
      </form>

      <div className="grid gap-3 sm:grid-cols-2">
        <Link to="/admin/staff" data-testid="link-staff" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-black">
          <Users className="h-5 w-5 text-[#9E1B22]" />
          <div><div className="text-sm font-semibold text-black">Equipo & Acceso</div><div className="text-xs text-slate-500">Dar o quitar admin a cuentas existentes.</div></div>
        </Link>
        <Link to="/admin/artists" data-testid="link-artists" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-black">
          <Music2 className="h-5 w-5 text-[#9E1B22]" />
          <div><div className="text-sm font-semibold text-black">Directorio de Artistas</div><div className="text-xs text-slate-500">Revisar, buscar, editar y fusionar artistas.</div></div>
        </Link>
      </div>
    </div>
  );
}
